import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../../../environments/environment';
import { ApiMethodService } from '../../../../tools/services/api/method-api/apiMethod.service';
import {
  ActionBackOfficeResponse,
  RegisterMemberByIdDto,
  RegisterMemberMarketingDashboardDto,
  RegisterMemberTableDto,
  RegisterMemberTableQuery,
  RegisterMemberVehicleOptionDto,
  UpdateRegisterMemberPayload,
  UpdateRegisterStatusPayload,
} from '../../../models/cityparking/register-member-list.model';

@Injectable({
  providedIn: 'root',
})
export class ApiRegisterMemberListService {
  private readonly baseUrl = environment.apiPrivateUrl;
  private readonly cookieService = inject(CookieService);
  private readonly apiMethodService = inject(ApiMethodService);
  private readonly http = inject(HttpClient);

  getRegisterMemberForTable(query: RegisterMemberTableQuery): Observable<RegisterMemberTableDto> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = {
      siteId: query.siteId,
      userId: query.userId,
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };

    if (query.searchField) params['searchField'] = query.searchField;
    if (query.searchText) params['searchText'] = query.searchText;
    if (query.status && query.status !== 'all') params['status'] = query.status;
    if (query.startDate) params['startDate'] = query.startDate;
    if (query.endDate) params['endDate'] = query.endDate;

    return this.apiMethodService.request<RegisterMemberTableDto>(
      '/backoffice/getRegisterMemberForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getRegisterMemberForTable',
        token,
        headerOnly: false,
        params,
      }
    );
  }

  getRegisterMemberMarketingDashboard(
    siteId: number,
    userId: number,
    startDate?: string,
    endDate?: string
  ): Observable<RegisterMemberMarketingDashboardDto> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = { siteId, userId };
    if (startDate) params['startDate'] = startDate;
    if (endDate) params['endDate'] = endDate;

    return this.apiMethodService.request<RegisterMemberMarketingDashboardDto>(
      '/backoffice/getRegisterMemberMarketingDashboard',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getRegisterMemberMarketingDashboard',
        token,
        headerOnly: false,
        params,
      }
    );
  }

  getRegisterMemberById(registerMemberId: number, userId: number): Observable<RegisterMemberByIdDto> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<RegisterMemberByIdDto>(
      '/backoffice/getRegisterMemberById',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getRegisterMemberById',
        token,
        headerOnly: false,
        params: { registerMemberId, userId },
      }
    );
  }

  getRegisterMemberVehiclesByCpUserId(
    cpUserId: number,
    userId: number,
    siteId: number
  ): Observable<RegisterMemberVehicleOptionDto[]> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<RegisterMemberVehicleOptionDto[]>(
      '/backoffice/getRegisterMemberVehiclesByCpUserId',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getRegisterMemberVehiclesByCpUserId',
        token,
        headerOnly: false,
        params: { cpUserId, userId, siteId },
      }
    );
  }

  updateRegisterMember(payload: UpdateRegisterMemberPayload): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/backoffice/updateRegisterMember',
      'PATCH',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'updateRegisterMember',
        token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  updateRegisterStatus(payload: UpdateRegisterStatusPayload): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/backoffice/updateRegisterStatus',
      'PATCH',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'updateRegisterStatus',
        token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  getRegisterMemberMarketingPdf(params: {
    siteId: number;
    startDate: string;
    endDate: string;
  }): Observable<Uint8Array> {
    const httpParams = new HttpParams()
      .set('siteId', params.siteId)
      .set('startDate', params.startDate)
      .set('endDate', params.endDate);

    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.cookieService.get('token')}`,
      Accept: 'application/octet-stream, application/pdf, */*',
    });

    return this.http
      .get(`${this.baseUrl}/Reports/register-member-marketing/pdf`, {
        headers,
        params: httpParams,
        responseType: 'arraybuffer',
      })
      .pipe(map((buffer) => new Uint8Array(buffer)));
  }
}
